/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: directRouteActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Direct Route module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a Direct Route entry in the form.
 * Pattern: /pbx/applications/direct-route/new?duplicate={id}
 */
export const getDirectRouteDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/direct-route/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given Direct Route id.
 */
export const navigateToDirectRouteDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getDirectRouteDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a Direct Route entry by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteDirectRouteById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/direct-route/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete Direct Route entry';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


